import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';

function Diseases() {
  const { letter } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tghNews, setTghNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [icd11Results, setIcd11Results] = useState([]);
  const [icd11Notice, setIcd11Notice] = useState(null);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const numbers = ['#'];

  const fetchNews = async () => {
    setNewsLoading(true);
    try {
      const response = await axios.get('/api/news');
      const news = Array.isArray(response.data)
        ? response.data
        : response.data?.news || [];

      // Sort newest first (aligns with News page)
      news.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTghNews(news);
    } catch (error) {
      console.error('Error fetching news:', error);
      setTghNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');

    if (searchParam) {
      setSearchQuery(searchParam);
      handleSearchDirect(searchParam);
    } else if (letter) {
      fetchDiseasesByLetter(letter);
    }
  }, [letter, location.search]);

  const handleSearchDirect = async (query) => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      setIcd11Notice(null);
      setIcd11Results([]);

      const [localRes, icdRes] = await Promise.allSettled([
        axios.get(`/api/diseases?search=${encodeURIComponent(query)}&enrich_icd11=false`),
        axios.get(`/api/diseases/icd/search?q=${encodeURIComponent(query)}&limit=20`)
      ]);

      if (localRes.status === 'fulfilled') {
        setSearchResults(localRes.value.data.diseases);
        setShowSearchResults(true);
      } else {
        console.error('Error searching diseases:', localRes.reason);
      }

      if (icdRes.status === 'fulfilled') {
        setIcd11Results(icdRes.value.data.results || []);
      } else {
        const data = icdRes.reason?.response?.data;
        setIcd11Results(Array.isArray(data?.results) ? data.results : []);
        setIcd11Notice(data?.hint || data?.error || null);
      }
    } catch (error) {
      console.error('Error searching diseases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiseasesByLetter = async (ltr) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/diseases?letter=${encodeURIComponent(ltr)}&enrich_icd11=false`
      );
      setDiseases(response.data.diseases || []);
    } catch (error) {
      console.error('Error fetching diseases:', error);
      setDiseases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search') || searchQuery;
    
    if (!searchParam.trim()) return;

    try {
      setIcd11Notice(null);
      setIcd11Results([]);

      const [localRes, icdRes] = await Promise.allSettled([
        axios.get(`/api/diseases?search=${encodeURIComponent(searchParam)}&enrich_icd11=false`),
        axios.get(`/api/diseases/icd/search?q=${encodeURIComponent(searchParam)}&limit=20`)
      ]);

      if (localRes.status === 'fulfilled') {
        setSearchResults(localRes.value.data.diseases);
        setShowSearchResults(true);
        setSearchQuery(searchParam);
      } else {
        console.error('Error searching diseases:', localRes.reason);
      }

      if (icdRes.status === 'fulfilled') {
        setIcd11Results(icdRes.value.data.results || []);
      } else {
        const data = icdRes.reason?.response?.data;
        setIcd11Results(Array.isArray(data?.results) ? data.results : []);
        setIcd11Notice(data?.hint || data?.error || null);
      }
    } catch (error) {
      console.error('Error searching diseases:', error);
    }
  };

  const handleLetterClick = (ltr) => {
    navigate(`/diseases/${ltr}`);
    setShowSearchResults(false);
    setSearchQuery('');
    window.scrollTo(0, 0);
  };

  // Group diseases by first letter(s) for display
  const groupDiseases = (diseaseList) => {
    const grouped = {};
    diseaseList.forEach(disease => {
      const key = disease.name.substring(0, 2).toUpperCase();
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(disease);
    });
    return grouped;
  };

  const groupedDiseases = groupDiseases(diseases);
  const groupedSearchResults = groupDiseases(searchResults);

  return (
    <>
      {/* Hero Section - Dark Blue Background */}
      <section className="tgh-diseases-hero py-5" style={{ backgroundColor: '#003d82', color: '#fff' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb" style={{ backgroundColor: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item"><Link to="/" style={{ color: '#fff' }}>Home</Link></li>
              <li className="breadcrumb-item"><span style={{ color: '#fff' }}>Diseases & Conditions</span></li>
              {letter && <li className="breadcrumb-item active" style={{ color: '#fff' }}>Begins with '{letter}'</li>}
            </ol>
          </nav>

          <h1 className="display-4 mb-3" style={{ fontFamily: 'Lora, serif', fontWeight: 'bold' }}>
            Diseases & Conditions
          </h1>
          <p className="lead mb-4" style={{ fontSize: '1.1rem' }}>
            Easy-to-understand answers about diseases and conditions
          </p>
          <p className="tgh-diseases-disclaimer mb-4" role="note">
            This is for informational purposes only. For medical advice or a diagnosis, consult a professional or
            healthcare provider.
          </p>

          <div className="row mt-4">
            <div className="col-lg-6">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="input-group input-group-lg">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search diseases & conditions"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ borderRadius: '4px 0 0 4px' }}
                  />
                  <button className="btn btn-light" type="submit" style={{ borderRadius: '0 4px 4px 0' }}>
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </form>
              {/* Removed ICD-11 loading indicator from search form - it shows in main loading area */}
            </div>
            <div className="col-lg-6">
              <div>
                <p className="mb-2" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  Find diseases & conditions by first letter
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {alphabet.map((ltr) => (
                    <button
                      key={ltr}
                      type="button"
                      className={`btn ${letter === ltr ? 'btn-light' : 'btn-outline-light'}`}
                      onClick={() => handleLetterClick(ltr)}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        fontWeight: '600',
                        border: letter === ltr ? '2px solid #fff' : '2px solid rgba(255,255,255,0.5)'
                      }}
                    >
                      {ltr}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`btn ${letter === '#' ? 'btn-light' : 'btn-outline-light'}`}
                    onClick={() => handleLetterClick('#')}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      fontWeight: '600',
                      border: letter === '#' ? '2px solid #fff' : '2px solid rgba(255,255,255,0.5)'
                    }}
                  >
                    #
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section - White Background */}
      <section className="py-5" style={{ backgroundColor: '#fff', minHeight: '60vh' }}>
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : showSearchResults ? (
            <>
              <h2 className="mb-4">Search Results for "{searchQuery}"</h2>

              {(icd11Results.length > 0 || icd11Notice) && (
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px', borderLeft: '4px solid #003d82' }}>
                  <div className="card-body p-4">
                    <h3 className="h5 mb-2" style={{ color: '#003d82' }}>
                      WHO ICD-11 (reference)
                    </h3>
                    <p className="text-muted small mb-3">
                      Official classification from the World Health Organization. Links open the ICD-11 browser in a new tab.
                    </p>
                    {icd11Notice && (
                      <p className="text-muted small mb-2">{icd11Notice}</p>
                    )}
                    {icd11Results.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {icd11Results.map((row, idx) => (
                          <li key={row.uri || idx} className="mb-2 pb-2 border-bottom border-light">
                            <div className="fw-semibold">{row.title}</div>
                            {row.code && (
                              <span className="badge bg-secondary me-2 mt-1">ICD-11 {row.code}</span>
                            )}
                            {row.browseUrl && (
                              <Link
                                to={`/diseases/detail/icd11-${encodeURIComponent(row.uri)}`}
                                className="small"
                                style={{ color: '#0066cc' }}
                              >
                                View full details <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
                              </Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : !icd11Notice && (
                      <p className="text-muted small mb-0">No ICD-11 matches for this term.</p>
                    )}
                  </div>
                </div>
              )}

              {searchResults.length === 0 ? (
                <p>No diseases found matching your search.</p>
              ) : (
                <div className="row">
                  <div className="col-lg-9">
                    <h3 className="h5 mb-3">TGH articles</h3>
                    {Object.keys(groupedSearchResults).sort().map((key) => (
                      <div key={key} className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{key}</h3>
                        <ul className="list-unstyled">
                          {groupedSearchResults[key].map((disease) => (
                            <li key={disease.id} className="mb-2">
                              {disease.alias ? (
                                <>
                                  <span>{disease.alias}</span>
                                  {' '}
                                  <Link to={`/diseases/detail/${disease.id}`} style={{ color: '#0066cc' }}>
                                    See {disease.name}
                                  </Link>
                                </>
                              ) : (
                                <Link to={`/diseases/detail/${disease.id}`} style={{ color: '#0066cc' }}>
                                  {disease.name}
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                        <a 
                          href="#top" 
                          onClick={(e) => {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          style={{ color: '#0066cc', fontSize: '0.9rem', textDecoration: 'none', cursor: 'pointer' }}
                        >
                          Back to top <i className="bi bi-arrow-up"></i>
                        </a>
                      </div>
                    ))}
                  </div>
                  <div className="col-lg-3">
                    <div className="border-start ps-4" style={{ borderColor: '#e0e0e0' }}>
                      <h6 className="mb-2">TGH Media</h6>
                      <p className="text-muted small mb-3">
                        Latest news and updates from Tema General Hospital.
                      </p>

                      {newsLoading ? (
                        <div className="text-center py-2">
                          <div className="spinner-border spinner-border-sm text-primary" role="status" />
                          <div className="small text-muted mt-2">Loading news...</div>
                        </div>
                      ) : tghNews.length === 0 ? (
                        <p className="text-muted small mb-0">No news articles available at this time.</p>
                      ) : (
                        <ul className="list-unstyled small" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
                          {tghNews.map((post) => (
                            <li key={post.id} className="mb-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <Link
                                to={`/news/${post.id}`}
                                style={{ color: '#0066cc', textDecoration: 'none', fontWeight: 600 }}
                              >
                                {post.title}
                              </Link>
                              {post.date && (
                                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                                  {formatDate(post.date)}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {letter && (
                <div className="row">
                  <div className="col-lg-9">
                    {diseases.length === 0 ? (
                      <div className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{letter}</h3>
                        <p>No diseases found starting with '{letter}'.</p>
                      </div>
                    ) : (
                      Object.keys(groupedDiseases).sort().map((key) => (
                        <div key={key} className="mb-5">
                          <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{key}</h3>
                          <ul className="list-unstyled">
                            {groupedDiseases[key].map((disease) => (
                              <li key={disease.id} className="mb-2">
                                {disease.alias ? (
                                  <>
                                    <span>{disease.alias}</span>
                                    {' '}
                                    <Link to={`/diseases/detail/${disease.id}`} style={{ color: '#0066cc' }}>
                                      See {disease.name}
                                    </Link>
                                  </>
                                ) : (
                                  <Link to={`/diseases/detail/${disease.id}`} style={{ color: '#0066cc' }}>
                                    {disease.name}
                                  </Link>
                                )}
                              </li>
                            ))}
                          </ul>
                          <a 
                            href="#top" 
                            onClick={(e) => {
                              e.preventDefault();
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            style={{ color: '#0066cc', fontSize: '0.9rem', textDecoration: 'none', cursor: 'pointer' }}
                          >
                            Back to top <i className="bi bi-arrow-up"></i>
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="col-lg-3">
                    <div className="border-start ps-4" style={{ borderColor: '#e0e0e0' }}>
                      <h6 className="mb-2">TGH Media</h6>
                      <p className="text-muted small mb-3">
                        Latest news and updates from Tema General Hospital.
                      </p>

                      {newsLoading ? (
                        <div className="text-center py-2">
                          <div className="spinner-border spinner-border-sm text-primary" role="status" />
                          <div className="small text-muted mt-2">Loading news...</div>
                        </div>
                      ) : tghNews.length === 0 ? (
                        <p className="text-muted small mb-0">No news articles available at this time.</p>
                      ) : (
                        <ul className="list-unstyled small" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
                          {tghNews.map((post) => (
                            <li key={post.id} className="mb-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <Link
                                to={`/news/${post.id}`}
                                style={{ color: '#0066cc', textDecoration: 'none', fontWeight: 600 }}
                              >
                                {post.title}
                              </Link>
                              {post.date && (
                                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                                  {formatDate(post.date)}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}

export default Diseases;
